import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  InputSignal,
  ModelSignal,
  OutputEmitterRef,
  Signal,
  TemplateRef,
  computed,
  effect,
  input,
  model,
  output,
  untracked,
  viewChild,
} from '@angular/core';
import { MatIcon } from '@angular/material/icon';
import { MatStep, MatStepContent, MatStepper, MatStepperIcon } from '@angular/material/stepper';

import { TwoStepEditActionsComponent } from './two-step-edit-actions/two-step-edit-actions.component';

@Component({
  selector: 'mp-two-step-edit',
  templateUrl: './two-step-edit.component.html',
  styleUrl: './two-step-edit.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'mp-two-step-edit',
  },
  imports: [
    NgTemplateOutlet,

    MatStep,
    MatStepContent,
    MatStepper,
    MatStepperIcon,
    MatIcon,

    TwoStepEditActionsComponent,
  ],
})
export class TwoStepEditComponent {
  private readonly stepper: Signal<MatStepper> = viewChild.required<MatStepper>('stepper');

  readonly firstStepTitle: InputSignal<string> = input<string>('');
  readonly secondStepTitle: InputSignal<string> = input<string>('');

  readonly firstStepEditable: InputSignal<boolean> = input<boolean>(true);

  readonly firstStepCompleted: InputSignal<boolean> = input.required<boolean>();
  readonly secondStepCompleted: InputSignal<boolean> = input.required<boolean>();

  readonly autoRepeatEnabled: InputSignal<boolean> = input<boolean>(true);

  readonly firstStepResultTemplate: InputSignal<TemplateRef<unknown>> = input.required<TemplateRef<unknown>>();
  readonly firstStepTemplate: InputSignal<TemplateRef<unknown>> = input.required<TemplateRef<unknown>>();
  readonly secondStepTemplate: InputSignal<TemplateRef<unknown>> = input.required<TemplateRef<unknown>>();

  readonly selectedStep: ModelSignal<number> = model<number>(0);

  readonly save: OutputEmitterRef<void> = output<void>();

  readonly saveAndContinue: OutputEmitterRef<void> = output<void>();

  readonly cancelEdit: OutputEmitterRef<void> = output<void>();

  protected readonly isFirstStep: Signal<boolean> = computed(() => this.selectedStep() === 0);

  protected readonly isFinalStep: Signal<boolean> = computed(() => this.selectedStep() === 1);

  readonly isSubmittable: Signal<boolean> = computed(() => this.firstStepCompleted() && this.secondStepCompleted());

  readonly stepperCssClass: Signal<Record<string, boolean>> = computed(() => {
    const activeStepClass = `stepper--active-step-${this.selectedStep() + 1}`;

    return { 'stepper--final-step': this.isFinalStep(), [activeStepClass]: true };
  });

  constructor() {
    effect(() => {
      const selectedStep: number = this.selectedStep();
      const stepper: MatStepper = this.stepper();
      // NOTE: Selected step cannot be assigned directly as stepper first need to reevaluate the step `completed` state
      untracked(() => setTimeout(() => (stepper.selectedIndex = selectedStep)));
    });
  }

  moveToStep(stepIndex: number): void {
    this.selectedStep.set(stepIndex);
  }
}
